<template>
  <!-- :model-value="true"
    no-parent-event -->
  <q-tooltip
    class="tooltip-standard"
    :class="classes"
    :anchor="anchor"
    :self="self"
    max-width="150px"
    :delay="500"
    :transition-show="transitionShow"
    :transition-hide="transitionHide"
  >
    <template
      v-for="(slot, slotName) in $slots"
      #[slotName]="scope"
      :key="slotName"
    >
      <slot
        :name="slotName"
        v-bind="scope || {}"
      />
    </template>
  </q-tooltip>
</template>

<script setup>
import { computed } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'Tooltip' })
const props = defineProps({
  position: { type: String, default: 'bottom' },
  type: {
    type: String,
    default: 'standard',
    validator: v => [
      'standard',
      'primary',
      'secondary',
      'info',
      'positive',
      'negative',
      'warning',
      'accent',
      'invert',
      'disabled'
    ].includes(v)
  }
})

const classes = computed(() => cx(
  props.type && `type--${props.type}`
))

const anchor = computed(() => {
  switch (props.position) {
    case 'top-left':
      return 'top left'
    case 'top':
      return 'bottom middle'
    case 'top-right':
      return 'top right'
    case 'right':
      return 'center right'
    case 'bottom-right':
      return 'bottom right'
    case 'bottom':
      return 'bottom middle'
    case 'bottom-left':
      return 'bottom left'
    case 'left':
      return 'center left'
    default:
      return 'bottom middle'
  }
})

const self = computed(() => {
  switch (props.position) {
    case 'top-left':
      return 'bottom right'
    case 'top':
      return 'top middle'
    case 'top-right':
      return 'bottom left'
    case 'right':
      return 'center left'
    case 'bottom-right':
      return 'top left'
    case 'bottom':
      return 'top middle'
    case 'bottom-left':
      return 'top left'
    case 'left':
      return 'center right'
    default:
      return 'top middle'
  }
})

const transitionShow = computed(() => {
  switch (props.position) {
    case 'top-left':
      return 'jump-up-left'
    case 'top':
      return 'jump-up'
    case 'top-right':
      return 'jump-up-right'
    case 'right':
      return 'jump-right'
    case 'bottom-right':
      return 'jump-down-right'
    case 'bottom':
      return 'jump-down'
    case 'bottom-left':
      return 'jump-down-left'
    case 'left':
      return 'jump-left'
    default:
      return 'jump-up'
  }
})

const transitionHide = computed(() => {
  switch (props.position) {
    case 'top-left':
      return 'jump-down-right'
    case 'top':
      return 'jump-down'
    case 'top-right':
      return 'jump-down-left'
    case 'right':
      return 'jump-left'
    case 'bottom-right':
      return 'jump-up-left'
    case 'bottom':
      return 'jump-up'
    case 'bottom-left':
      return 'jump-up-right'
    case 'left':
      return 'jump-right'
    default:
      return 'jump-down'
  }
})

</script>

<style>
@reference "#app-styles.css";

.tooltip-standard {
  @apply text-[10px] leading-3 font-medium tracking-wide ink-soft;
  @apply surface-overlay p-xs rounded-sm shadow-sm;

  & > div {
    @apply flex flex-col gap-xs;

    .badge-standard { @apply min-w-8 max-w-full truncate; }
  }

  &.type--standard {}
  &.type--primary { @apply text-white bg-primary-faint border-primary-fainter; }
  &.type--secondary { @apply text-white bg-secondary-faint border-secondary-fainter; }
  &.type--info { @apply text-white bg-info-faint border-info-fainter; }
  &.type--positive { @apply text-white bg-positive-faint border-positive-fainter; }
  &.type--negative { @apply text-white bg-negative-faint border-negative-fainter; }
  &.type--warning { @apply text-white bg-warning-faint border-warning-fainter; }
  &.type--accent { @apply text-white bg-accent-faint border-accent-fainter; }
  &.type--invert { @apply text-ink-invert-soft bg-layer-invert-base border-edge-invert-base; }
  &.type--disabled { @apply ink-disabled bg-secondary-fainter border-secondary-faintest; }

}
</style>
